import { createSlice } from '@reduxjs/toolkit';
import { createAsyncSaga } from 'create-async-saga';
import { set, get, isArray, clone, update } from 'lodash';
import { call, takeEvery, all, fork } from 'redux-saga/effects';
import service from './service';

export const fetchData = createAsyncSaga(
  '@form/fetchData',
  function* (request) {
    return yield call(service.fetchData, request);
  }
);

export function* watchFetchData() {
  yield takeEvery(fetchData.actionType, fetchData.asyncSaga);
}

export const preloadData = createAsyncSaga(
  '@form/preloadData',
  function* (requests) {
    return yield all(
      requests.map((request) => call(service.fetchData, request))
    );
  }
);

export function* watchPreloadData() {
  yield takeEvery(preloadData.actionType, preloadData.asyncSaga);
}

export const submitData = createAsyncSaga(
  '@form/submitData',
  function* (request) {
    return yield call(service.submitData, request);
  }
);

export function* watchSubmitData() {
  yield takeEvery(submitData.actionType, submitData.asyncSaga);
}

const updateLoading = (state, payload, isLoading) => {
  let newPayload = clone(payload);
  let item = newPayload;
  if (isArray(payload)) {
    item = newPayload.shift();
  }
  const { metadata, request } = item || {};
  if (metadata?.form && request?.name) {
    set(state, `${metadata?.form}.loading.${request.name}`, isLoading);
  }
  if (newPayload?.length > 0) {
    updateLoading(state, newPayload, isLoading);
  }
};

export const formSlice = createSlice({
  name: '@form',
  initialState: {},
  reducers: {
    setValues: (state, action) => {
      if (action?.payload) {
        const {
          metadata: { form },
          data,
        } = action.payload;
        state[form].values = data;
      }
    },
    initializeForm: (state, action) => {
      if (action?.payload) {
        const {
          metadata: { form },
        } = action.payload;
        set(state, `${form}.initalTime`, new Date().getTime());
      }
    },
    release: (state, action) => {
      if (action?.payload) {
        const {
          metadata: { form },
        } = action.payload;
        delete state[form];
        return state;
      }
    },
    reset: (state, action) => {
      if (action?.payload) {
        const {
          metadata: { form },
          data: { initialValues = {}, delay = 500 },
        } = action.payload;
        const initalTime = get(state, `${form}.initalTime`);
        if (initalTime && new Date().getTime() - initalTime > delay) {
          return update(state, `${form}`, () => ({
            initialValues,
            initalTime: new Date().getTime(),
          }));
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchData.fulfilled, (state, action) => {
      const { data, formName } = action.payload;
      set(state, `${formName}.values`, data);
      set(state, `${formName}.initialValues`, data);
    });
    builder.addCase(preloadData.fulfilled, (state, action) => {
      action.payload.forEach((payload) => {
        const { formName, name, data } = payload;
        set(state, `${formName}.data.${name}`, data);
      });
    });
    builder.addCase(submitData.fulfilled, (state, action) => {
      const { data, name, formName } = action.payload;

      return set(state, `${formName}.commits.${name}`, {
        ...data,
        requestId: action?.meta?.requestId,
      });
    });
    builder
      .addMatcher(
        (action) =>
          action.type.startsWith('@form') && action.type.endsWith('/pending'),
        (state, action) => {
          const form = get(action, 'meta.arg', undefined);
          return updateLoading(state, form, true);
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith('@form') && action.type.endsWith('/rejected'),
        (state, action) => {
          const form = get(action, 'meta.arg', undefined);
          return updateLoading(state, form, false);
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith('@form') && action.type.endsWith('/fulfilled'),
        (state, action) => {
          const form = get(action, 'meta.arg', undefined);
          return updateLoading(state, form, false);
        }
      );
  },
});

export const { setValues, initializeForm, reset, release } = formSlice.actions;

export function* formSaga() {
  yield all([
    fork(watchFetchData),
    fork(watchPreloadData),
    fork(watchSubmitData),
  ]);
}

export default formSlice.reducer;
